import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, X, Check, Camera,
  MapPin, Store, CheckCircle2, LayoutDashboard, Pencil,
} from "lucide-react";

import {
  upsertCommercePartner,
  uploadCommercePartnerLogo,
  listenToUserCommercePartners,
} from "../../redux/commercePartnersSlice";
import type { AppDispatch, RootState } from "../../redux/store";

/* ═══════════════════════════════════════════════════════════
   WAIT FOR GOOGLE MAPS
═══════════════════════════════════════════════════════════ */
function waitForGoogleMaps(): Promise<void> {
  return new Promise((resolve) => {
    if ((window as any).google?.maps?.places) { resolve(); return; }
    const interval = setInterval(() => {
      if ((window as any).google?.maps?.places) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — identical to Glamour
═══════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#FAFAFA",
  bgMuted:    "#F4F4F2",
  black:      "#0A0A0A",
  blackMid:   "#1C1C1C",
  blackSoft:  "#2C2C2C",
  border:     "#E6E6E4",
  borderDark: "#C8C8C4",
  muted:      "#8A8A86",
  mutedSoft:  "#B8B8B4",
  gold:       "#C9A96E",
  goldLight:  "#F0E2C8",
  goldBg:     "#FBF6EE",
};

/* ═══════════════════════════════════════════════════════════
   STEPS
═══════════════════════════════════════════════════════════ */
const STEPS = [
  { id: 1, label: "Identity",  sub: "Your store name"   },
  { id: 2, label: "Visuals",   sub: "Logo & branding"   },
  { id: 3, label: "Location",  sub: "Where you operate" },
  { id: 4, label: "Launch",    sub: "Review & go live"  },
];

/* ═══════════════════════════════════════════════════════════
   ADDRESS STATE
═══════════════════════════════════════════════════════════ */
interface AddressState {
  street:       string;
  city:         string;
  country:      string;
  county:       string;
  neighborhood: string;
  latitude:     number | undefined;
  longitude:    number | undefined;
}

const EMPTY_ADDRESS: AddressState = {
  street: "", city: "", country: "", county: "",
  neighborhood: "", latitude: undefined, longitude: undefined,
};

/* ═══════════════════════════════════════════════════════════
   ANIMATION VARIANTS
═══════════════════════════════════════════════════════════ */
const pv = {
  enter:  { opacity: 0, y: 36, filter: "blur(6px)" },
  center: { opacity: 1, y: 0,  filter: "blur(0px)" },
  exit:   { opacity: 0, y: -24, filter: "blur(4px)" },
};
const stagger = { center: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } } };
const fu = {
  enter:  { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.48, ease: [0.22, 1, 0.36, 1] as any } },
};

/* ═══════════════════════════════════════════════════════════
   SHARED UI PRIMITIVES
═══════════════════════════════════════════════════════════ */
const inputBaseStyle: React.CSSProperties = {
  flex: 1, border: "none", outline: "none", padding: "12px 14px",
  fontSize: 13, fontFamily: "'Montserrat', sans-serif",
  color: T.black, background: "transparent",
};

const LField: React.FC<{
  label: string; required?: boolean; children: React.ReactNode; hint?: string;
}> = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 22 }}>
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: T.muted,
      fontFamily: "'Montserrat', sans-serif", marginBottom: 7, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.gold, marginLeft: 3 }}>✦</span>}
    </div>
    {children}
    {hint && (
      <div style={{
        fontSize: 10, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif",
        marginTop: 5, letterSpacing: "0.04em",
      }}>
        {hint}
      </div>
    )}
  </div>
);

const LInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  prefix?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement>;
  readOnly?: boolean;
}> = ({ value, onChange, placeholder, type = "text", prefix, inputRef, readOnly }) => (
  <div
    style={{
      display: "flex", border: `1px solid ${T.border}`,
      background: readOnly ? T.bgMuted : T.bgSoft, transition: "border-color .2s",
      opacity: readOnly ? 0.7 : 1,
    }}
    onFocus={e => { if (!readOnly) e.currentTarget.style.borderColor = T.black; }}
    onBlur={e  => { e.currentTarget.style.borderColor = T.border; }}
  >
    {prefix && (
      <div style={{
        padding: "0 12px", display: "flex", alignItems: "center",
        background: T.bgMuted, borderRight: `1px solid ${T.border}`,
        color: T.muted, flexShrink: 0,
      }}>
        {prefix}
      </div>
    )}
    <input
      ref={inputRef}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      readOnly={readOnly}
      style={{ ...inputBaseStyle, cursor: readOnly ? "not-allowed" : "text" }}
    />
  </div>
);

const StepLabel: React.FC<{ step: number }> = ({ step }) => (
  <div style={{
    fontSize: 9, fontWeight: 700, letterSpacing: "0.28em", color: T.muted,
    fontFamily: "'Montserrat', sans-serif", marginBottom: 12,
  }}>
    STEP 0{step} / 0{STEPS.length}
  </div>
);

const H1style = (isMobile: boolean): React.CSSProperties => ({
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: isMobile ? 32 : 40, fontWeight: 300,
  color: T.black, letterSpacing: "0.03em",
  lineHeight: 1.05, margin: "0 0 8px",
});

const SubStyle: React.CSSProperties = {
  fontSize: 12, color: T.muted,
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 300, lineHeight: 1.8, marginBottom: 28,
};

/* ═══════════════════════════════════════════════════════════
   PARTNER CARD (list mode)
═══════════════════════════════════════════════════════════ */
const PartnerCard: React.FC<{
  partner: any;
  onEdit: () => void;
}> = ({ partner, onEdit }) => {
  const initials = (partner.name ?? "C")
    .split(" ").slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        border: `1px solid ${T.border}`, padding: "14px 16px",
        marginBottom: 10, background: T.bg,
      }}
    >
      <div style={{ width: 3, height: 52, background: T.black, flexShrink: 0 }}/>

      <div style={{
        width: 50, height: 50, background: T.bgMuted, overflow: "hidden",
        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {partner.image
          ? <img src={partner.image} alt={partner.name} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
          : <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 300, color: T.muted }}>{initials}</span>
        }
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 500, color: T.black,
          fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.03em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {partner.name || "Unnamed Store"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
          {partner.address?.city && (
            <span style={{
              fontSize: 10, color: T.muted, fontFamily: "'Montserrat', sans-serif",
              display: "flex", alignItems: "center", gap: 3,
            }}>
              <MapPin size={9}/>{partner.address.city}
            </span>
          )}
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            color: T.bg, background: T.black, padding: "2px 7px",
            fontFamily: "'Montserrat', sans-serif",
          }}>ACTIVE</span>
        </div>
      </div>

      <motion.button
        whileHover={{ scale: 1.03 }} whileTap={{ scale: .97 }}
        onClick={onEdit}
        style={{
          background: T.bg, border: `1px solid ${T.border}`,
          color: T.black, padding: "8px 14px", cursor: "pointer",
          fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
          fontFamily: "'Montserrat', sans-serif",
          display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
          transition: "all .18s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background  = T.black;
          e.currentTarget.style.color       = T.bg;
          e.currentTarget.style.borderColor = T.black;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background  = T.bg;
          e.currentTarget.style.color       = T.black;
          e.currentTarget.style.borderColor = T.border;
        }}
      >
        <Pencil size={11}/> EDIT
      </motion.button>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const CommerceOnboarding: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const logoRef         = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  const { user }    = useSelector((s: RootState) => s.auth);
  const { partners } = useSelector((s: RootState) => s.commercePartners);
  const userId      = user?.userId ?? "";

  const hasPartner = partners.length > 0;

  const [mode,     setMode]     = useState<"list" | "form">(hasPartner ? "list" : "form");
  const [step,     setStep]     = useState(1);
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  const [mapsReady, setMapsReady] = useState(!!(window as any).google?.maps?.places);

  /* ── Form state ── */
  const [storeName,    setStoreName]    = useState("");
  const [logoPreview,  setLogoPreview]  = useState<string | null>(null);
  const [address,      setAddress]      = useState<AddressState>(EMPTY_ADDRESS);

  /* ── Resize ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── Auth guard ── */
  useEffect(() => { if (!user) navigate("/AuthSwitch"); }, [user]);

  /* ── Listen to partners ── */
  useEffect(() => {
    if (userId) dispatch(listenToUserCommercePartners(userId) as any);
  }, [userId, dispatch]);

  /* ── Sync list mode ── */
  useEffect(() => {
    if (partners.length > 0 && mode === "form" && !saving && !success) setMode("list");
  }, [partners]);

  /* ── Wait for Google Maps ── */
  useEffect(() => {
    if (mapsReady) return;
    waitForGoogleMaps().then(() => setMapsReady(true));
  }, []);

  /* ── Bind Autocomplete on step 3 ── */
  useEffect(() => {
    if (mode !== "form" || step !== 3 || !mapsReady) return;

    const tid = setTimeout(() => {
      const input = addressInputRef.current;

      if (autocompleteRef.current) {
        (window as any).google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      if (!input) return;

      const autocomplete = new (window as any).google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        fields: ["address_components", "geometry", "formatted_address"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;

        const comps = place.address_components as Array<{ types: string[]; long_name: string }>;
        const get   = (type: string) => comps.find(c => c.types.includes(type))?.long_name ?? "";

        const resolved: AddressState = {
          street:       `${get("street_number")} ${get("route")}`.trim(),
          city:         get("locality") || get("administrative_area_level_2"),
          neighborhood: get("sublocality") || get("neighborhood"),
          country:      get("country"),
          county:       get("administrative_area_level_1"),
          latitude:     place.geometry?.location?.lat(),
          longitude:    place.geometry?.location?.lng(),
        };

        setAddress(resolved);

        if (partnerId) {
          dispatch(upsertCommercePartner({ brandId: partnerId, data: { address: resolved } }) as any);
        }
      });

      autocompleteRef.current = autocomplete;
    }, 150);

    return () => clearTimeout(tid);
  }, [mode, step, mapsReady, partnerId]);

  /* ── Reset autocomplete when leaving step 3 ── */
  useEffect(() => {
    if (step !== 3 && autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, [step]);

  /* ── Success redirect ── */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => { setSuccess(false); setMode("list"); }, 1800);
    return () => clearTimeout(t);
  }, [success]);

  /* ── Reset & enter form ── */
  const enterForm = () => {
    setStoreName(""); setLogoPreview(null);
    setAddress(EMPTY_ADDRESS); setError(null); setStep(1);
    setPartnerId(null);
    if (autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    setMode("form");
  };

  /* ── Logo upload ── */
  const handleLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!partnerId) return;
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoPreview(URL.createObjectURL(f));
    setUploading(true);
    dispatch(uploadCommercePartnerLogo({ brandId: partnerId, file: f }) as any)
      .unwrap()
      .catch(() => alert("Logo upload failed. Please try again."))
      .finally(() => setUploading(false));
  };

  /* ── Save early (step 1 → 2) ── */
  const saveEarly = async (): Promise<boolean> => {
    if (!storeName.trim()) { setError("Store name is required."); return false; }
    try {
      setSaving(true); setError(null);
      const created = await dispatch(upsertCommercePartner({
        brandId: partnerId ?? undefined,
        data: { name: storeName, isDraft: true, isComplete: false },
      })).unwrap();
      setPartnerId(created.brandId ?? created.partnerId);
      return true;
    } catch {
      setError("Could not save store. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /* ── Final submit (step 4) ── */
  const handleFinalSubmit = async () => {
    if (!partnerId) { setError("Store not yet saved. Please go back and try again."); return; }
    try {
      setSaving(true); setError(null);
      await dispatch(upsertCommercePartner({
        brandId: partnerId,
        data: {
          address: address.city ? address : undefined,
          isDraft: false, isComplete: true,
        },
      })).unwrap();
      setSuccess(true);
    } catch {
      setError("Registration failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Step navigation ── */
  const goNext = async () => {
    setError(null);
    if (step === 1) {
      if (!storeName.trim()) { setError("Store name is required."); return; }
      const saved = await saveEarly();
      if (!saved) return;
      setStep(2); return;
    }
    if (step === 2) { setStep(3); return; }
    if (step === 3) { setStep(4); return; }
    if (step === 4) { await handleFinalSubmit(); }
  };

  const goPrev = () => {
    setError(null);
    if (step > 1) setStep(s => s - 1);
    else setMode("list");
  };

  /* ═══════════════════════════════════════════════════════
     LIST MODE
  ═══════════════════════════════════════════════════════ */
  const renderList = () => (
    <motion.div key="list" variants={pv} initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: 9, letterSpacing: "0.25em", color: T.muted,
          fontFamily: "'Montserrat', sans-serif", fontWeight: 600, marginBottom: 10,
        }}>
          COMMERCE SELLER
        </div>
        <h1 style={H1style(isMobile)}>My Store</h1>
        <p style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
          {partners.length} registered store{partners.length !== 1 ? "s" : ""}
        </p>
      </div>

      {partners.map((p: any) => (
        <PartnerCard
          key={p.brandId ?? p.partnerId}
          partner={p}
          onEdit={() => navigate(`/MetaCommerceDashboard/${userId}`)}
        />
      ))}

      <div style={{ height: 1, background: T.border, margin: "20px 0 18px" }}/>

      <motion.button
        whileHover={{ scale: 1.01 }} whileTap={{ scale: .98 }}
        onClick={() => navigate(`/MetaCommerceDashboard/${userId}`)}
        style={{
          width: "100%", background: T.black, color: T.bg,
          border: "none", padding: "14px 0", cursor: "pointer",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.2em",
          fontFamily: "'Montserrat', sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          marginBottom: 10,
        }}
      >
        <LayoutDashboard size={14}/> GO TO DASHBOARD
      </motion.button>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 1 — STORE IDENTITY
  ═══════════════════════════════════════════════════════ */
  const renderStep1 = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={1}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1style(isMobile)}>Store Identity</h1>
        <p style={SubStyle}>Choose a name that represents your commerce brand to customers.</p>
      </motion.div>
      <motion.div variants={fu}>
        <LField label="Store Name" required>
          <LInput value={storeName} onChange={setStoreName} placeholder="e.g. Horizon Market"/>
        </LField>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 2 — VISUALS
  ═══════════════════════════════════════════════════════ */
  const renderStep2 = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={2}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1style(isMobile)}>Store Logo</h1>
        <p style={SubStyle}>Upload your store logo. This is what customers see first.</p>
      </motion.div>

      <motion.div variants={fu} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <label
          style={{
            width: 120, height: 120,
            background: logoPreview ? "transparent" : T.bgMuted,
            border: `1px dashed ${T.borderDark}`,
            cursor: partnerId ? "pointer" : "not-allowed",
            opacity: partnerId ? 1 : 0.5,
            overflow: "hidden",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 8,
            position: "relative",
          }}
          onClick={e => { if (!partnerId) e.preventDefault(); }}
        >
          {logoPreview
            ? <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
            : <>
                <Camera size={24} color={T.muted}/>
                <span style={{ fontSize: 9, color: T.muted, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>UPLOAD LOGO</span>
              </>
          }
          {uploading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", border: `3px solid ${T.black}`, borderTopColor: "transparent", animation: "glamSpin 0.8s linear infinite" }}/>
            </div>
          )}
          <input ref={logoRef} type="file" hidden accept="image/*" onChange={handleLogo}/>
        </label>

        {!partnerId && (
          <div style={{ fontSize: 10, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>
            Save store name first to unlock logo upload
          </div>
        )}

        {partnerId && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
              background: T.goldBg, border: `1px solid ${T.goldLight}`,
            }}
          >
            <Check size={12} color={T.gold}/>
            <span style={{ fontSize: 10, color: "#7A5C00", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>
              Store saved — you can now upload a logo
            </span>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 3 — LOCATION
  ═══════════════════════════════════════════════════════ */
  const renderStep3 = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={3}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1style(isMobile)}>Location</h1>
        <p style={SubStyle}>Where is your store based? This helps match you with nearby customers.</p>
      </motion.div>

      <motion.div variants={fu}>
        {!mapsReady && (
          <div style={{
            fontSize: 11, color: T.gold, fontFamily: "'Montserrat', sans-serif",
            padding: "10px 14px", background: T.goldBg, border: `1px solid ${T.goldLight}`,
            marginBottom: 14, letterSpacing: "0.06em",
          }}>
            Loading address search…
          </div>
        )}

        <LField label="Business Address" hint="Start typing your address and select from the dropdown">
          <div style={{ display: "flex", border: `1px solid ${T.border}`, background: T.bgSoft }}>
            <div style={{ padding: "0 12px", display: "flex", alignItems: "center", background: T.bgMuted, borderRight: `1px solid ${T.border}`, color: T.muted, flexShrink: 0 }}>
              <MapPin size={14}/>
            </div>
            <input
              ref={addressInputRef}
              value={address.street}
              onChange={e => setAddress(a => ({ ...a, street: e.target.value }))}
              placeholder={mapsReady ? "Enter your address…" : "Loading maps…"}
              type="text"
              autoComplete="off"
              style={{
                ...inputBaseStyle,
                pointerEvents: mapsReady ? "auto" : "none",
                opacity: mapsReady ? 1 : 0.5,
              }}
            />
          </div>
        </LField>

        {address.city && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: -10 }}
          >
            {[
              { label: "City",         val: address.city         },
              { label: "Country",      val: address.country      },
              { label: "Region",       val: address.county       },
              { label: "Neighborhood", val: address.neighborhood },
            ].filter(f => f.val).map(f => (
              <div key={f.label}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.15em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 5 }}>
                  {f.label.toUpperCase()}
                </div>
                <div style={{ fontSize: 13, color: T.black, fontFamily: "'Montserrat', sans-serif", padding: "10px 14px", background: T.bgMuted, border: `1px solid ${T.border}` }}>
                  {f.val}
                </div>
              </div>
            ))}
            {address.latitude !== undefined && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.15em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 5 }}>
                  COORDINATES
                </div>
                <div style={{ fontSize: 11, color: T.muted, fontFamily: "'Montserrat', sans-serif", padding: "8px 14px", background: T.bgMuted, border: `1px solid ${T.border}`, letterSpacing: "0.04em" }}>
                  {address.latitude?.toFixed(6)}, {address.longitude?.toFixed(6)}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 4 — REVIEW & LAUNCH
  ═══════════════════════════════════════════════════════ */
  const renderStep4 = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={4}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1style(isMobile)}>Review & Launch</h1>
        <p style={SubStyle}>Everything looks good. Confirm your details and go live as a Commerce seller.</p>
      </motion.div>

      <motion.div variants={fu} style={{ border: `1px solid ${T.border}`, background: T.bg, marginBottom: 24, overflow: "hidden" }}>
        {/* Logo strip */}
        <div style={{ height: 80, background: T.bgMuted, position: "relative" }}>
          <div style={{ position: "absolute", bottom: -24, left: 20, width: 48, height: 48, background: T.bg, border: `2px solid ${T.bg}`, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,.12)" }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              : <div style={{ width: "100%", height: "100%", background: T.bgMuted, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Store size={18} color={T.muted}/>
                </div>
            }
          </div>
        </div>
        <div style={{ padding: "32px 20px 20px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 400, color: T.black, marginBottom: 12 }}>
            {storeName}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            {[
              ["City",    address.city    || "—"],
              ["Country", address.country || "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 3 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize: 12, color: T.black, fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div variants={fu} style={{ padding: "12px 14px", background: T.goldBg, border: `1px solid ${T.goldLight}`, marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: "#7A5C00", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em", lineHeight: 1.7 }}>
          By registering, you agree to Commerce Seller Terms. Your store will be reviewed before going live.
        </div>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     SUCCESS OVERLAY
  ═══════════════════════════════════════════════════════ */
  const SuccessOverlay = () => (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ position: "absolute", inset: 0, background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 20, gap: 16, padding: 32 }}
    >
      <motion.div
        initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 160, damping: 14 }}
        style={{ width: 72, height: 72, borderRadius: "50%", border: `2px solid ${T.black}`, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <motion.svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.black} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <motion.path d="M5 13l4 4L19 7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.4, duration: 0.6, ease: "easeOut" }}/>
        </motion.svg>
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, color: T.black, margin: 0, letterSpacing: "0.04em", fontStyle: "italic" }}
      >
        Store Registered
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", textAlign: "center", margin: 0, lineHeight: 1.7 }}
      >
        Welcome to Commerce Seller. Redirecting…
      </motion.p>
      <motion.div style={{ width: 160, height: 2, background: T.bgMuted, overflow: "hidden", marginTop: 16 }}>
        <motion.div
          initial={{ width: "0%" }} animate={{ width: "100%" }}
          transition={{ delay: 0.4, duration: 1.4, ease: "linear" }}
          style={{ height: "100%", background: T.black }}
        />
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP CONTENT MAP
  ═══════════════════════════════════════════════════════ */
  const stepContent: Record<number, () => React.ReactNode> = {
    1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
  };

  /* ═══════════════════════════════════════════════════════
     ROOT RENDER
  ═══════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0A0A0A; }
        input::placeholder, textarea::placeholder { color: #B8B8B4; }
        ::-webkit-scrollbar { display: none; }
        @keyframes glamSpin { to { transform: rotate(360deg); } }
        .pac-container {
          font-family: 'Montserrat', sans-serif !important; font-size: 12px !important;
          border: 1px solid #E6E6E4 !important; border-top: none !important;
          box-shadow: 0 8px 24px rgba(0,0,0,.08) !important; border-radius: 0 !important;
          z-index: 99999 !important;
        }
        .pac-item { padding: 10px 14px !important; border-top: 1px solid #F4F4F2 !important; color: #2C2C2C !important; cursor: pointer !important; line-height: 1.5 !important; }
        .pac-item:hover, .pac-item-selected { background: #FAFAFA !important; }
        .pac-item-query { font-size: 12px !important; color: #0A0A0A !important; font-weight: 500 !important; }
        .pac-icon { display: none !important; }
        .pac-matched { color: #C9A96E !important; font-weight: 700 !important; }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #0A0A0A 0%, #1C1C1C 50%, #0A0A0A 100%)",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        padding: isMobile ? "16px" : "40px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: 560 }}>

          {/* Top bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "rgba(255,255,255,.4)", fontFamily: "'Montserrat', sans-serif",
                fontSize: 11, letterSpacing: "0.12em",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <ArrowLeft size={14}/> BACK
            </button>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, letterSpacing: "0.3em", color: "#FFFFFF" }}>
              COMMERCE
            </div>
            <div style={{ width: 80 }}/>
          </div>

          {/* Card */}
          <div style={{
            background: T.bg,
            padding: isMobile ? "28px 20px 32px" : "40px 36px 44px",
            position: "relative", overflow: "hidden", minHeight: 400,
          }}>
            {/* Progress bar */}
            {mode === "form" && !success && (
              <div style={{ display: "flex", gap: 4, marginBottom: 32 }}>
                {STEPS.map(s => (
                  <motion.div
                    key={s.id}
                    animate={{
                      flex: step >= s.id ? 2 : 1,
                      background: step > s.id ? T.black : step === s.id ? T.blackSoft : T.border,
                    }}
                    transition={{ duration: 0.45 }}
                    style={{ height: 2, borderRadius: 1 }}
                  />
                ))}
              </div>
            )}

            <AnimatePresence mode="wait">
              {mode === "list" && (
                <motion.div key="list" variants={pv} initial="enter" animate="center" exit="exit" transition={{ duration: 0.38 }}>
                  {renderList()}
                </motion.div>
              )}

              {mode === "form" && !success && (
                <motion.div
                  key={`step-${step}`} variants={pv} initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                >
                  {stepContent[step]?.()}

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "10px 14px", background: "#FFF5F5", border: "1px solid #FECACA",
                        marginBottom: 14, marginTop: 4,
                      }}
                    >
                      <X size={12} color="#8B0000"/>
                      <span style={{ fontSize: 11, color: "#8B0000", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}>{error}</span>
                    </motion.div>
                  )}

                  {/* Nav buttons */}
                  <div style={{ display: "grid", gridTemplateColumns: step > 1 ? "1fr 2fr" : "1fr", gap: 10, marginTop: 20 }}>
                    {step > 1 && (
                      <button
                        onClick={goPrev}
                        style={{
                          padding: "14px 0", border: `1px solid ${T.border}`,
                          background: T.bg, color: T.muted, cursor: "pointer",
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
                          fontFamily: "'Montserrat', sans-serif",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          transition: "all .2s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = T.black; e.currentTarget.style.color = T.black; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}
                      >
                        <ArrowLeft size={12}/> BACK
                      </button>
                    )}
                    <button
                      onClick={goNext}
                      disabled={saving || uploading}
                      style={{
                        padding: "14px 0", border: "none",
                        background: (saving || uploading) ? T.muted : T.black,
                        color: T.bg, cursor: (saving || uploading) ? "wait" : "pointer",
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.2em",
                        fontFamily: "'Montserrat', sans-serif",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        transition: "opacity .2s",
                      }}
                    >
                      {saving
                        ? <><span style={{ width: 12, height: 12, border: `2px solid ${T.bg}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "glamSpin 1s linear infinite" }}/> SAVING…</>
                        : uploading
                          ? <><span style={{ width: 12, height: 12, border: `2px solid ${T.bg}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "glamSpin 1s linear infinite" }}/> UPLOADING…</>
                          : step === STEPS.length
                            ? <><CheckCircle2 size={13}/> LAUNCH STORE</>
                            : <>CONTINUE <ChevronRight size={12}/></>
                      }
                    </button>
                  </div>

                  <div style={{ textAlign: "center", marginTop: 16, fontSize: 9, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.2em" }}>
                    {STEPS[step - 1]?.label.toUpperCase()} — {step} of {STEPS.length}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {success && <SuccessOverlay/>}
          </div>

          {/* Desktop step indicator */}
          {mode === "form" && !isMobile && (
            <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 0 }}>
              {STEPS.map((s, i) => {
                const done   = step > s.id;
                const active = step === s.id;
                return (
                  <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        border: `1.5px solid ${done || active ? "rgba(255,255,255,.7)" : "rgba(255,255,255,.2)"}`,
                        background: done ? "rgba(255,255,255,.9)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, fontWeight: 700, fontFamily: "'Montserrat', sans-serif",
                        color: done ? T.black : active ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.3)",
                        transition: "all .3s",
                      }}>
                        {done ? <Check size={10} color={T.black}/> : s.id}
                      </div>
                      <span style={{
                        fontSize: 8, letterSpacing: "0.15em",
                        color: active ? "rgba(255,255,255,.8)" : "rgba(255,255,255,.25)",
                        fontFamily: "'Montserrat', sans-serif", fontWeight: active ? 700 : 400,
                      }}>
                        {s.label.toUpperCase()}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div style={{
                        width: 40, height: 1,
                        background: done ? "rgba(255,255,255,.5)" : "rgba(255,255,255,.1)",
                        margin: "0 6px", marginBottom: 20, transition: "background .3s",
                      }}/>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommerceOnboarding;